import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { reportError } from '@/lib/observability'

/**
 * Top-level boundary — the last line of defence. Anything reaching here is a bug,
 * so it is reported and the user is offered a reload rather than a blank page.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack ?? undefined })
  }

  override render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6 text-center">
        <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-danger-subtle text-danger">
          <AlertTriangle className="size-5" aria-hidden />
        </div>
        <h1 className="text-lg font-semibold text-fg">Something went wrong</h1>
        <p className="mt-1 max-w-md text-sm text-fg-muted">
          The application hit an unexpected error and could not recover. Reloading
          usually clears it.
        </p>
        <Button variant="primary" className="mt-4" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    )
  }
}
